import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { defer as observableDefer, Observable, of as observableOf, throwError as observableThrowError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { HttpStatusCode } from '../../dtos/http-status-code';
import { ItineraryChangeRequest } from '../../dtos/request/itinerary-request/itinerary-change-request';
import { ItineraryErrorCode } from '../../dtos/response/itinerary-response/itinerary-error-code';
import { ItineraryResponse } from '../../dtos/response/itinerary-response/itinerary-response';
import { timeoutError } from '../../models/timeout-error';
import { TimeoutLimit } from '../../models/timeout-limit';
import { GlobalEvent, GlobalEventService } from '../global-event-service/global-event.service';
import { isInvalidSabreUserIdAndPass, isPasswordDecryptionError } from '../../utils/error-helper';
import { UpdateItineraryAndStorePqrRequest } from '../../dtos/request/voluntary-reissue/update-itinerary/update-itinerary-and-store-pqr-request';
import { UpdateItineraryAndStorePqrResponse } from '../../dtos/response/voluntary-reissue/update-itinerary/update-itinerary-and-store-pqr-response';
import { InvolUpdateItinRequest } from '../../dtos/request/involuntary-change/invol-update-itin-request';
import { InvolUpdateItinResponse } from '../../dtos/response/involuntary-change/invol-update-itin-response';

export interface ItineraryServiceAPI {
  changeItineraryAndRevalidateTicket(request: ItineraryChangeRequest): Observable<ItineraryResponse>;
  changeItineraryAndReissueTicket(request: ItineraryChangeRequest): Observable<ItineraryResponse>;
  updateItineraryAndStorePqr(request: UpdateItineraryAndStorePqrRequest): Observable<UpdateItineraryAndStorePqrResponse>;
  involUpdateItin(request: InvolUpdateItinRequest): Observable<InvolUpdateItinResponse>;
}

@Injectable({
  providedIn: 'root',
})
export class ItineraryService implements ItineraryServiceAPI {
  private readonly http = inject(HttpClient);
  private readonly eventService = inject(GlobalEventService);

  changeItineraryAndRevalidateTicket(request: ItineraryChangeRequest): Observable<ItineraryResponse> {
    const options = {
      headers: new HttpHeaders({
        background: 'false',
      }),
    };
    return this.http.put<ItineraryResponse>(`api/itinerary/revalidate`, request, options).pipe(
      timeout({
        each: TimeoutLimit.MEDIUM,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((itineraryResponse) => itineraryResponse),
      catchError((err: HttpErrorResponse) => {
        if (timeoutError.statusText === err.statusText) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
          return observableOf({
            statusCode: HttpStatusCode.GATEWAY_TIMEOUT,
            errorCode: ItineraryErrorCode.TIMEOUT,
            exceptionContent: 'timeout',
          });
        }
        if (isInvalidSabreUserIdAndPass(err.error?.exceptionContent)) {
          return observableOf({
            statusCode: err.status,
            errorCode: ItineraryErrorCode.NULL_TOKEN_ERROR,
            exceptionContent: err.error?.exceptionContent,
          } as ItineraryResponse);
        }
        if (isPasswordDecryptionError(err.error?.exceptionContent)) {
          return observableOf({
            statusCode: err.status,
            errorCode: ItineraryErrorCode.PASSWORD_DECRYPTION_ERROR,
            exceptionContent: err.error?.exceptionContent,
          } as ItineraryResponse);
        }
        return observableOf({
          statusCode: err.status,
          errorCode: err.error?.errorCode ?? ItineraryErrorCode.SYSTEM_FAILURE,
          exceptionContent: err.error?.exceptionContent,
          reservation: err.error?.reservation,
        } as ItineraryResponse);
      })
    );
  }

  changeItineraryAndReissueTicket(request: ItineraryChangeRequest): Observable<ItineraryResponse> {
    const options = {
      headers: new HttpHeaders({
        background: 'false',
      }),
    };
    return this.http.put<ItineraryResponse>(`api/itinerary/reissue`, request, options).pipe(
      timeout({
        each: TimeoutLimit.MEDIUM,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((itineraryResponse) => itineraryResponse),
      catchError((err: HttpErrorResponse) => {
        if (timeoutError.statusText === err.statusText) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
          return observableOf({ statusCode: HttpStatusCode.GATEWAY_TIMEOUT, errorCode: ItineraryErrorCode.TIMEOUT });
        }
        if (isInvalidSabreUserIdAndPass(err.error?.exceptionContent)) {
          return observableOf({
            statusCode: err.status,
            errorCode: ItineraryErrorCode.NULL_TOKEN_ERROR,
            exceptionContent: err.error?.exceptionContent,
          } as ItineraryResponse);
        }
        if (isPasswordDecryptionError(err.error?.exceptionContent)) {
          return observableOf({
            statusCode: err.status,
            errorCode: ItineraryErrorCode.PASSWORD_DECRYPTION_ERROR,
            exceptionContent: err.error?.exceptionContent,
          } as ItineraryResponse);
        } else {
          return observableOf({
            statusCode: err.status,
            errorCode: err.error?.errorCode ?? ItineraryErrorCode.SYSTEM_FAILURE,
            exceptionContent: err.error?.exceptionContent,
            newTicketNumbers: err.error?.newTicketNumbers,
            failedToReissueTicketNumbers: err.error?.failedToReissueTicketNumbers,
            reservation: err.error?.reservation,
          } as ItineraryResponse);
        }
      })
    );
  }

  updateItineraryAndStorePqr(request: UpdateItineraryAndStorePqrRequest): Observable<UpdateItineraryAndStorePqrResponse> {
    const options = {
      headers: new HttpHeaders({
        background: 'false',
      }),
    };
    return this.http.put<UpdateItineraryAndStorePqrResponse>(`api/itinerary/update-itin/pqr`, request, options).pipe(
      timeout({
        each: TimeoutLimit.LONG,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      map((response) => response),
      catchError((err: HttpErrorResponse) => {
        if (timeoutError.statusText === err.statusText) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
          return observableOf({
            pauseUpgradeEngineResponse: null,
            priceQuoteReissueResponse: null,
            problemDetails: { detail: 'timeout' },
          } as UpdateItineraryAndStorePqrResponse);
        }
        return observableOf({
          pauseUpgradeEngineResponse: null,
          priceQuoteReissueResponse: null,
          problemDetails: { detail: err.error },
        } as UpdateItineraryAndStorePqrResponse);
      })
    );
  }

  involUpdateItin(request: InvolUpdateItinRequest): Observable<InvolUpdateItinResponse> {
    const options = {
      headers: new HttpHeaders({
        background: 'false',
      }),
    };
    return this.http.put<InvolUpdateItinResponse>(`api/itinerary/involuntary-changes/update-itin`, request, options).pipe(
      timeout({
        each: TimeoutLimit.LONG,
        with: () => observableDefer(() => observableThrowError(() => new HttpErrorResponse(timeoutError))),
      }),
      catchError((err: HttpErrorResponse) => {
        if (timeoutError.statusText === err.statusText) {
          this.eventService.broadcastAjax(GlobalEvent.AJAX_END, err);
          return observableOf({ failureSlices: [], problemDetails: { detail: 'timeout' } } as InvolUpdateItinResponse);
        }
        return observableOf({ failureSlices: [], problemDetails: { detail: err.error } } as InvolUpdateItinResponse);
      })
    );
  }
}
